import { css } from '@emotion/react';
import { ConditionalWrap } from 'components/conditional-wrap';
import React, { SetStateAction, Dispatch, createContext, useContext, useEffect, useState, useRef } from 'react';
import { AngularServicesContext } from 'react-app';
import { useSelector } from 'react-redux';
import { cloneDeep, kebabCase, mergeWith } from 'lodash';
import { ComponentTrueType, ComponentType, LectureComponent } from 'redux/schemas/models/lecture-component';
import { getFlatCourseAliases } from 'redux/selectors/course';
import { useAppDispatch } from 'redux/store';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import useErrorBoundary from 'use-error-boundary';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { DeepPartial } from 'utility-types';
import { replaceArrays } from 'shared/lodash-utils';
import { NovoAIItemType } from 'redux/schemas/models/lecture-page';
import { adminWorkflowNavigate, ModalWorkflow, WorkflowParams, WorkflowMode, WorkflowSave } from '.';
import getDefaultNewComponentData from '../data/new-component-defaults';
import AngularModalContent from './angular-modal-content';
import { ComponentMetadata } from '../data';

export type ModalWorkflowSettings = {
  /** The translation function to call to get the modal title. Receives course aliases as input */
  title: (mode: WorkflowMode, courseAliases?) => string,
  modalWidth?: number,
  modalStyles?: ReturnType<typeof css>,
  bodyClassName?: string,
  /** If set, will render an Angularjs controller into this modal as a child of renderModal() */
  angularConfig?: {
    /** The string name of the Angularjs controller. Defaults to 'LectureComponentModalCtrl */
    controllerFunction?: any, // This 'should' be Parameters<IControllerService>[0], but Typescript is unable to infer param types from overloads: https://github.com/microsoft/TypeScript/issues/32164
    templateUrl: string,
    /** Many of the Angularjs component modals specify a different form name for inputs in the modal
     * TODO: We could refactor these all to be the same instead of using this property */
    formName?: string,
  }
};

/** This context should be used to customize the workflow's presentation after it's initally created. For instance, this allows new component modals to change
 * the modal's title text */
export const ModalWorkflowContext = createContext<{
  mode: WorkflowMode,
  settings: ModalWorkflowSettings,
  setSettings: Dispatch<SetStateAction<ModalWorkflowSettings>>,
  isSaving: boolean,
}>(null);

/** The core behavior for the ModalWorkflow, but is always wrapped by ConditionalWrapModalWorkflow */
export type ModalWorkflowHandlerProps = {
  metadata: ComponentMetadata,
  workflow: ModalWorkflow,
  lectureComponent: Partial<LectureComponent>,
  children: React.ReactNode,
  save: WorkflowSave,
  mode: WorkflowMode,
  useExistingData?: boolean;
};

const ModalWorkflowHandler = (props: ModalWorkflowHandlerProps) => {
  const dispatch = useAppDispatch();
  const { ErrorBoundary } = useErrorBoundary();
  const [show, setShow] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState(props.workflow.initialSettings);
  const params = useLecturePageParams();
  const currentCourse = useSelector(state => state.models.courses[state.app.currentCatalogId]);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const forwardedOnModalCloseRef = useRef<(e: Event, closeModal: Function) => void>();

  // Whether or not this is an Angularjs managed modal workflow
  const usingAngular = !!settings.angularConfig;
  const angularServices = useContext(AngularServicesContext);

  const bodyClassName = [
    settings.angularConfig ? 'modal-body' : '',
    settings.bodyClassName ?? '',
  ].filter(Boolean).join(' ');

  // TODO: See the todo below; this is a hack and needs removed
  const getLectureComponent = () => {
    // The angularjs model only exists when we're editing; this might need to change per comments below
    if (props.mode === 'edit'
      && usingAngular
      && angularServices.$scope[`lectureComponent${props.lectureComponent.id}`]
    ) {
      const lectureComponentFromAngular = angularServices.$scope[`lectureComponent${props.lectureComponent.id}`];

      if (
        ([NovoAIItemType.DISCUSSION, NovoAIItemType.SUGGEST_ACTIVITIES].includes(props.lectureComponent.aiOrigin))
        && props.useExistingData
      ) {
        lectureComponentFromAngular.isRegenerating = true;
        lectureComponentFromAngular.postDraft.body = (props.lectureComponent as LectureComponent<ComponentType.TOPIC>).topic.body;
      }

      return lectureComponentFromAngular;
    }
    return props.lectureComponent;
  };

  /* TODO: My original design for this has broken down. The original intent was for the lecture component object here to always be a flat, non-instance object
  But, the angularjs modals need an instance of a lecture component model b/c they assume they can call various instance methods on the component. This draft was intended to be the "working state", non-saved version of this component which doesn't make sense when you're mutating an instance
  For now I'm just going to instance this to the angularjs lecture component model when we're using an AngularJS modal controller and will revisit this when we build React modal UIs here */
  const [lectureComponentDraft, setLectureComponentDraft] = useState(getLectureComponent());

  const closeModal = () => {
    if (show) setShow(false);
  };

  const onConfirm = (
    localLectureComponent?: DeepPartial<LectureComponent>,
    skipSave?: boolean,
    customLectureComponent?: ComponentTrueType,
    customWorkflow?: ModalWorkflow<ComponentTrueType>,
  ) => {
    const workflow = mergeWith(cloneDeep(props.workflow), customWorkflow, replaceArrays);

    if (skipSave) {
      closeModal();
      return Promise.resolve();
    }

    setIsSaving(true);
    return props.save(localLectureComponent, customLectureComponent, props.lectureComponent, props.metadata).then(actionResult => {
      closeModal();

      // Unwrap the lc data returned from the action. If payload doesn't exist then this was returned via Angular
      // TODO: Very brittle & likely to break, see if we can rework this
      const resultActionComponent = (actionResult?.payload ?? actionResult) as LectureComponent;

      // Navigate to the defined Admin 1.0 page after creation, if set
      const postCreateActions = () => {
        const extraDeps = usingAngular ? { angularServices } : undefined;

        const lectureComponent = localLectureComponent;

        mergeWith(lectureComponent, resultActionComponent, replaceArrays);

        workflow.afterSave?.(dispatch, props.mode, lectureComponent, extraDeps);

        if (props.mode === 'new') {
          adminWorkflowNavigate(workflow, params.catalogId, resultActionComponent as any);
        }
      };

      // TODO: This is a legacy step for Angularjs components that need to show a modal after creation.
      // Long-term we should make this a configurable part of the workflow
      const postCreateModalTemplate = workflow.showCreateConfirmation?.(resultActionComponent, currentCourse, props.mode);
      if (postCreateModalTemplate) {
        angularServices.$uibModal.open({
          templateUrl: postCreateModalTemplate,
          backdropClass: 'modal-overlay-backdrop',
          windowClass: 'modal-overlay',
          keyboard: false,
          controller: 'AttachModalResolvesToVmCtrl as vm',
          resolve: {
            vmResolves: {
              CurrentCourseManager: angularServices.CurrentCourseManager,
              lectureComponent: resultActionComponent,
            },
          },
        }).closed.then(() => {
          postCreateActions();
        });
      } else {
        postCreateActions();
      }
    }).finally(() => {
      setIsSaving(false);
    });
  };

  const onCancel = () => {
    closeModal();
  };

  const onModalClose = (e) => {
    forwardedOnModalCloseRef.current?.(e, closeModal);

    if (!e.defaultPrevented) {
      if (settings.angularConfig) {
        angularServices.$scope.$broadcast('react.modal.closing');
      } else {
        onCancel();
      }
    }
  };

  useEffect(() => {
    if (show) angularServices.$scope.$on('modal.content.closed', closeModal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset the draft back to the initial lectureComponent data
  useEffect(() => {
    if (!show) {
      // TODO: I think we want to cloneDeep here when not using an angularjs modal
      setLectureComponentDraft(getLectureComponent());
    }
  }, [props.lectureComponent, show]);

  return (
    <div>
      <ModalWorkflowContext.Provider value={{ mode: props.mode, settings, setSettings, isSaving }}>
        <NvModal
          header={settings.title(props.mode, courseAliases)}
          body={(
            <ErrorBoundary
              render={() => props.workflow.renderModal({
                ...props,
                onCancel,
                onConfirm,
                forwardOnModalClose: (fn) => {
                  forwardedOnModalCloseRef.current = fn;
                },
                // TODO: We should only be displaying AngularModalContent if the angular settings are defined
                children: (
                  <AngularModalContent
                    {...props}
                    lectureComponent={lectureComponentDraft}
                    onConfirm={onConfirm}
                    onCancel={onCancel}
                  />),
              })}
              renderError={({ error }) => <div className='border border-danger'>{error.message}</div>}
            />
            )}
          type={ModalType.DYNAMIC}
          fullHeight={false}
          show={show}
          width={settings.modalWidth}
          modalStyles={settings.modalStyles}
          onClose={(e) => onModalClose(e)}
          // Apply different CSS classes to the NvModal elements if we're rendering AngularJS modal content. This makes us use the B3 classe for the body (non-prefixed) and compent specific class for the base element
          rootClassName={settings.angularConfig ? `lecture-component-modal ${kebabCase(props.lectureComponent.type)}-modal` : ''}
          bodyClassName={bodyClassName}
        />
      </ModalWorkflowContext.Provider>
      <div onClick={() => setShow(true)}>
        {props.children}
      </div>
    </div>
  );
};

/**
 * A component that conditionally wraps its children with a Modal Workflow based on the provided workflow type
 * @param {WorkflowParams} props - The properties passed to the component
 * @returns {JSX.Element} - The rendered component with or without the Modal Workflow
 */
const ConditionalWrapModalWorkflow: React.FC<WorkflowParams> = ({
  metadata,
  mode,
  lectureComponent,
  componentType,
  save,
  children,
  useExistingData = false,
}) => {
  // Check if we need to display the Modal Workflow
  const hasModalWorkflow = metadata?.workflow.type === 'modal';

  // The initial data set for the new component; gets passed into the Angularjs modals if they're specified and eventually passed into createNewComponent() to post against the API
  const initialLectureComponent = mode === 'edit' || useExistingData ? lectureComponent : getDefaultNewComponentData(componentType);

  return (
    <ConditionalWrap
      condition={hasModalWorkflow}
      wrap={hasModalWorkflow && ((modalChildren) => (
        <ModalWorkflowHandler
          metadata={metadata}
          lectureComponent={initialLectureComponent as Partial<LectureComponent>}
          workflow={metadata.workflow as ModalWorkflow}
          save={(savedLc, customType, originalLc, metadataLc) => save(savedLc, customType, originalLc, metadataLc)}
          mode={mode}
          useExistingData={useExistingData}
        >
          {modalChildren}
        </ModalWorkflowHandler>
      ))}
    >
      {children}
    </ConditionalWrap>
  );
};

export default ConditionalWrapModalWorkflow;
